import type {
  StoreBarcodeLookupResult,
  StoreCapabilities,
  StoreReportResponse,
  StoreSale,
  StoreSummaryResponse,
} from "@/types/services/api-contracts";

import { callEdgeData } from "./_core/edge-call";
import { STORE_FUNCTION_NAME } from "./_core/function-names";

export const storeService = {
  async getSummary(venueId: string): Promise<StoreSummaryResponse> {
    return callEdgeData<StoreSummaryResponse>(STORE_FUNCTION_NAME, "summary", {
      method: "GET",
      params: { venue_id: venueId },
    });
  },

  async getReport(data: {
    venue_id: string;
    from: string;
    to: string;
  }): Promise<StoreReportResponse> {
    return callEdgeData<StoreReportResponse>(STORE_FUNCTION_NAME, "report", {
      method: "GET",
      params: {
        venue_id: data.venue_id,
        from: data.from,
        to: data.to,
      },
    });
  },

  async lookupBarcode(data: {
    venue_id: string;
    barcode: string;
  }): Promise<StoreBarcodeLookupResult> {
    return callEdgeData<StoreBarcodeLookupResult>(STORE_FUNCTION_NAME, "lookup-barcode", {
      method: "GET",
      params: {
        venue_id: data.venue_id,
        barcode: data.barcode,
      },
    });
  },

  async upsertProduct(data: {
    venue_id: string;
    product_id?: string;
    name: string;
    category_name?: string;
    category_code?: string;
    unit?: string;
    price_cents: number;
    cost_cents?: number | null;
    barcode?: string;
    stock_tracking?: boolean;
    allow_negative_stock?: boolean;
    min_stock?: number;
    active?: boolean;
  }): Promise<{ id: string; internal_code?: string }> {
    return callEdgeData<{ id: string; internal_code?: string }>(STORE_FUNCTION_NAME, "upsert-product", {
      body: data,
    });
  },

  async adjustStock(data: {
    venue_id: string;
    product_id: string;
    quantity_delta: number;
    reason: string;
  }): Promise<{
    movement_id: string;
    product_id: string;
    quantity_before: number;
    quantity_after: number;
  }> {
    return callEdgeData<{
      movement_id: string;
      product_id: string;
      quantity_before: number;
      quantity_after: number;
    }>(STORE_FUNCTION_NAME, "adjust-stock", {
      body: data,
    });
  },

  async openCash(data: {
    venue_id: string;
    opening_amount_cents: number;
    notes?: string;
  }): Promise<{ id: string }> {
    return callEdgeData<{ id: string }>(STORE_FUNCTION_NAME, "open-cash", {
      body: data,
    });
  },

  async closeCash(data: {
    venue_id: string;
    cash_session_id: string;
    counted_amount_cents: number;
    notes?: string;
  }): Promise<{ id: string; difference_amount_cents: number }> {
    return callEdgeData<{ id: string; difference_amount_cents: number }>(STORE_FUNCTION_NAME, "close-cash", {
      body: data,
    });
  },

  async recordSale(data: {
    venue_id: string;
    cash_session_id: string;
    items: Array<{ product_id: string; quantity: number }>;
    payment_method: "pix" | "cash" | "debit" | "credit" | "courtesy";
    notes?: string;
  }): Promise<{
    sale_id: string;
    sale_code: string;
    total_cents: number;
    status?: StoreSale["status"];
    payment_status?: StoreSale["payment_status"];
    checkout_url?: string | null;
    provider_checkout_id?: string | null;
    provider_payment_intent_id?: string | null;
    checkout_expires_at?: string | null;
  }> {
    return callEdgeData<{
      sale_id: string;
      sale_code: string;
      total_cents: number;
      status?: StoreSale["status"];
      payment_status?: StoreSale["payment_status"];
      checkout_url?: string | null;
      provider_checkout_id?: string | null;
      provider_payment_intent_id?: string | null;
      checkout_expires_at?: string | null;
    }>(STORE_FUNCTION_NAME, "record-sale", {
      body: data,
    });
  },

  async cancelSale(data: {
    venue_id: string;
    sale_id: string;
    reason: string;
  }): Promise<{
    sale_id: string;
    sale_code: string;
    reversed_total_cents: number;
    status: "cancelled";
  }> {
    return callEdgeData<{
      sale_id: string;
      sale_code: string;
      reversed_total_cents: number;
      status: "cancelled";
    }>(STORE_FUNCTION_NAME, "cancel-sale", {
      body: data,
    });
  },

  async updatePermissions(data: {
    venue_id: string;
    user_id: string;
    permissions: StoreCapabilities;
  }): Promise<{ user_id: string }> {
    return callEdgeData<{ user_id: string }>(STORE_FUNCTION_NAME, "update-permissions", {
      body: data,
    });
  },
};
